import React from 'react'
import {
  Router,
  Route,
  Redirect,
  IndexRedirect,
  IndexRoute,
  browserHistory,
  applyRouterMiddleware
} from 'react-router'
import { useScroll } from 'react-router-scroll'
import scrollBehavior from './lib/scroll-behavior'
import ErrorTracker from './lib/error-tracker'
import { isOnLatest } from './lib/version-checker'
import loadable from 'hoc/Loadable'

const WelcomeApp = loadable(() =>
  import(/* webpackChunkName: "Welcome" */ './containers/WelcomeApp')
)
const NotFound = loadable(() =>
  import(/* webpackChunkName: "NotFound" */ 'ui/NotFound')
)
const LoginApp = loadable(() =>
  import(/* webpackChunkName: "LoginApp" */ './containers/LoginApp')
)
const Logout = loadable(() =>
  import(/* webpackChunkName: "Logout" */ './containers/Logout')
)
const SignupApp = loadable(() =>
  import(/* webpackChunkName: "SignupApp" */ './containers/SignupApp')
)

const SignupAppV2 = loadable(() =>
  import(/* webpackChunkName: "SignUpAppV2" */ './containers/SignUpAppV2')
)

const WhatToTrack = loadable(() =>
  import(/* webpackChunkName: "WhatToTrack" */ './containers/WhatToTrack')
)

const ForgotApp = loadable(() =>
  import(/* webpackChunkName: "ForgotApp" */ './containers/ForgotApp')
)
const ResetApp = loadable(() =>
  import(/* webpackChunkName: "ResetApp" */ './containers/ResetApp')
)
const ReceiptApp = loadable(() =>
  import(/* webpackChunkName: "ReceiptApp" */ './containers/ReceiptApp')
)
const WorkspaceApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceApp" */ './containers/WorkspaceApp')
)
const AccountApp = loadable(() =>
  import(/* webpackChunkName: "AccountApp" */ './containers/Account/AccountApp')
)
const CustomersApp = loadable(() =>
  import(/* webpackChunkName: "CustomersApp" */ './containers/Customers/App')
)
const CustomersCreateApp = loadable(() =>
  import(/* webpackChunkName: "CustomersCreateApp" */ './containers/Customers/CreateApp')
)
const CustomersSettings = loadable(() =>
  import(/* webpackChunkName: "CustomersSettings" */ './components/Customers/Settings')
)
const CustomersSettingsSourcesApp = loadable(() =>
  import(/* webpackChunkName: "CustomersSettingsSourcesApp" */ './containers/Customers/Settings/SourcesApp')
)
const CustomersSettingsAPIAccessApp = loadable(() =>
  import(/* webpackChunkName: "CustomersSettingsAPIAccessApp" */ './containers/Customers/Settings/APIAccessApp')
)
const CustomersDestinationsApp = loadable(() =>
  import(/* webpackChunkName: "CustomersDestinationsApp" */ './containers/Customers/DestinationsApp')
)
const CustomersDestinationsViewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersDestinationsViewApp" */ './containers/Customers/DestinationsApp/ViewApp')
)
const CustomersAudiencesListApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesListApp" */ './containers/Customers/Audiences/ListApp')
)
const CustomersAudiencesViewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesViewApp" */ './containers/Customers/Audiences/Audience/ViewApp')
)
const CustomersAudiencesOverviewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesOverviewApp" */ './containers/Customers/Audiences/Audience/OverviewApp')
)
const CustomersAudiencesEditApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesEditApp" */ './containers/Customers/Audiences/Audience/EditApp')
)
const CustomersAudiencesSettingsApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesSettingsApp" */ './containers/Customers/Audiences/Audience/SettingsApp')
)
const CustomersAudiencesCreateApp = loadable(() =>
  import(/* webpackChunkName: "CustomersAudiencesCreateApp" */ './containers/Customers/Audiences/CreateApp')
)
const CustomersTraitsListApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsListApp" */ './containers/Customers/Traits/ListApp')
)
const CustomersTraitsCreateApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsCreateApp" */ './containers/Customers/Traits/CreateApp')
)
const CustomersTraitsViewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsViewApp" */ './containers/Customers/Traits/ViewApp')
)
const CustomersTraitsOverviewApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsOverviewApp" */ './containers/Customers/Traits/OverviewApp/OverviewApp')
)
const CustomersTraitsSettingsApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsSettingsApp" */ './containers/Customers/Traits/SettingsApp')
)
const CustomersTraitsEditApp = loadable(() =>
  import(/* webpackChunkName: "CustomersTraitsEditApp" */ './containers/Customers/Traits/EditApp')
)
const CustomersExplorerApp = loadable(() =>
  import(/* webpackChunkName: "CustomersExplorerApp" */ './containers/Customers/ExplorerApp')
)
const CustomersProfileSheetApp = loadable(() =>
  import(/* webpackChunkName: "CustomersProfileSheetApp" */ './containers/Customers/ProfileSheetApp')
)

const IntegrationsCatalogDetailApp = loadable(() =>
  import(/* webpackChunkName: "IntegrationsCatalogDetailApp" */ './containers/IntegrationsCatalog/DetailsApp')
)
const DestinationsCatalogApp = loadable(() =>
  import(/* webpackChunkName: "DestinationsCatalogApp" */ './containers/IntegrationsCatalogV2/DestinationsCatalogApp')
)
const SourcesCatalogApp = loadable(() =>
  import(/* webpackChunkName: "SourcesCatalogApp" */ './containers/IntegrationsCatalogV2/SourcesCatalogApp')
)
const WorkspaceOverviewApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceOverviewApp" */ './containers/WorkspaceOverviewApp')
)
const WorkspaceQuickStart = loadable(() =>
  import(/* webpackChunkName: "WorkspaceQuickStart" */ './containers/WorkspaceQuickStart/v2')
)
const WorkspaceQuickStartDestinationSelection = loadable(() =>
  import(/* webpackChunkName: "WorkspaceQuickStartDestinationSelection" */ './containers/WorkspaceQuickStart/v2/DestinationStepSelection')
)
const WorkspaceQuickStartDestinationConfig = loadable(() =>
  import(/* webpackChunkName: "WorkspaceQuickStartDestinationConfig" */ './containers/WorkspaceQuickStart/v2/DestinationStepConfig')
)
const WorkspaceQuickStartSourceInstall = loadable(() =>
  import(/* webpackChunkName: "WorkspaceQuickStartSourceInstall" */ './containers/WorkspaceQuickStart/v2/SourceStepInstall')
)

const WorkspaceSettings = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettings" */ './containers/WorkspaceSettings/App')
)
const WorkspaceSettingsBasicApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsBasicApp" */ './containers/WorkspaceSettings/BasicApp')
)
const WorkspaceSettingsUsageApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsUsageApp" */ './containers/WorkspaceSettings/UsageApp')
)
const WorkspaceSettingsActivityFeedApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsActivityFeedApp" */ './containers/WorkspaceSettings/ActivityFeedApp')
)

const WorkspaceSettingsNotificationsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsNotificationsApp" */ './containers/WorkspaceSettings/NotificationsApp')
)
const WorkspaceSettingsConnectionsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionsApp" */ './containers/WorkspaceSettings/Authentication/ConnectionsApp')
)
const WorkspaceSettingsConnectionEditApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionEditApp" */ './containers/WorkspaceSettings/Authentication/ConnectionEditApp')
)
const WorkspaceSettingsConnectionSetupApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupApp')
)
const WorkspaceSettingsConnectionSetupSelectApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupSelectApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupSelectApp')
)
const WorkspaceSettingsConnectionSetupConnectionApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupConnectionApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupConnectionApp')
)
const WorkspaceSettingsConnectionSetupIDPApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupIDPApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupIDPApp')
)
const WorkspaceSettingsConnectionSetupCompleteApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsConnectionSetupCompleteApp" */ './containers/WorkspaceSettings/Authentication/ConnectionSetupCompleteApp')
)
const WorkspaceSettingsDomainsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsDomainsApp" */ './containers/WorkspaceSettings/Authentication/DomainsApp')
)
const WorkspaceSettingsAdvancedApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsAdvancedApp" */ './containers/WorkspaceSettings/Authentication/AdvancedApp')
)
const WorkspaceSettingsApiKeyApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsApiKeyApp" */ './containers/WorkspaceSettings/Authentication/ApiKeysApp/index')
)
const WorkspaceSettingsTeamApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsTeamApp" */ './containers/WorkspaceSettings/TeamApp')
)
const WorkspaceSettingsPlansApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlansApp" */ './containers/WorkspaceSettings/PlansApp')
)
const WorkspaceSettingsCardApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsCardApp" */ './containers/WorkspaceSettings/CardApp')
)
const WorkspaceSettingsPaymentsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPaymentsApp" */ './containers/WorkspaceSettings/PaymentsApp')
)
const WorkspaceSettingsPlanChangeApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlanChangeApp" */ './containers/WorkspaceSettings/PlanChangeApp')
)
const WorkspaceSettingsPlanChangeSelectApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlanChangeSelectApp" */ './containers/WorkspaceSettings/PlanChangeSelectApp')
)
const WorkspaceSettingsPlanChangeConfirmApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlanChangeConfirmApp" */ './containers/WorkspaceSettings/PlanChangeConfirmApp')
)
const WorkspaceSettingsPlanChangeThanksApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPlanChangeThanksApp" */ './containers/WorkspaceSettings/PlanChangeThanksApp')
)
const WorkspaceSettingsDeletionApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsDeletionApp" */ './containers/WorkspaceSettings/WorkspaceChurn/WorkspaceDeleteApp')
)
const WorkspaceSettingsDowngradeApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsDowngradeApp" */ './containers/WorkspaceSettings/WorkspaceChurn/WorkspaceDowngradeApp')
)
const WorkspaceSettingsPrivacySecurityApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsPrivacySecurityApp" */ './containers/WorkspaceSettings/PrivacySecurityApp')
)
const WorkspaceSettingsSuppressedUsersApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsSuppressedUsersApp" */ './containers/WorkspaceSettings/SuppressedUsersApp')
)
const WorkspaceSettingsDeletionRequestsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceSettingsDeletionRequestsApp" */ './containers/WorkspaceSettings/DeletionRequestsApp')
)

const WorkspaceDestinationsApp = loadable(() =>
  import(/* webpackChunkName: "WorkspaceDestinationsApp" */ './containers/WorkspaceDestinationsApp')
)
const WorkspacesHome = loadable(() =>
  import(/* webpackChunkName: "WorkspacesHome" */ './containers/WorkspacesHome')
)
const WorkspacesNew = loadable(() =>
  import(/* webpackChunkName: "WorkspacesNew" */ './containers/WorkspacesNew')
)

const EnableApp = loadable(() =>
  import(/* webpackChunkName: "EnableApp" */ './containers/EnableApp')
)

const SourcesApp = loadable(() =>
  import(/* webpackChunkName: "SourcesApp" */ './containers/Sources/App')
)
const LegacySourceSetup = loadable(() =>
  import(/* webpackChunkName: "LegacySourceSetup" */ './containers/Sources/SourceSetup/legacy/LegacySourceSetup')
)
const SourceApp = loadable(() =>
  import(/* webpackChunkName: "SourceApp" */ './containers/Sources/SourceApp')
)
const SourceDebugger = loadable(() =>
  import(/* webpackChunkName: "SourceDebugger" */ './containers/Sources/SourceDebuggerApp')
)
const DestinationDetails = loadable(() =>
  import(/* webpackChunkName: "DestinationDetails" */ './containers/Destinations/DestinationDetails')
)
const DestinationOverview = loadable(() =>
  import(/* webpackChunkName: "DestinationOverview" */ './containers/Destinations/DestinationOverview')
)
const DestinationConfig = loadable(() =>
  import(/* webpackChunkName: "DestinationConfig" */ './components/Destinations/DestinationConfig')
)
const DestinationDebugger = loadable(() =>
  import(/* webpackChunkName: "DestinationDebugger" */ './components/Destinations/DestinationDebugger')
)
const DestinationErrorSheet = loadable(() =>
  import(/* webpackChunkName: "DestinationErrorSheet" */ './containers/Destinations/DestinationErrors/ErrorSheet')
)
const DestinationEventDelivery = loadable(() =>
  import(/* webpackChunkName: "DestinationEventDelivery" */ './containers/Destinations/DestinationEventDelivery')
)
const SourceOverviewApp = loadable(() =>
  import(/* webpackChunkName: "SourceOverviewApp" */ './containers/Sources/SourceOverview/SourceOverviewApp')
)
const WarehouseSimulatorApp = loadable(() =>
  import(/* webpackChunkName: "WarehouseSimulatorApp" */ './containers/WarehouseSimulator/WarehouseSimulatorApp')
)
const SourceOverviewRun = loadable(() =>
  import(/* webpackChunkName: "SourceOverviewRun" */ './containers/Sources/SourceOverview/SourceOverviewRun')
)
const SourceSchemaOld = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaOld" */ './containers/Sources/SourceSchema/legacy')
)
const ViolationsApp = loadable(() =>
  import(/* webpackChunkName: "ViolationsApp" */ './containers/Sources/ViolationsApp/index.js')
)
const ViolationSummarySheet = loadable(() =>
  import(/* webpackChunkName: "ViolationSummarySheet" */ './containers/Sources/ViolationsApp/ViolationSummarySheet.js')
)
const ProtocolsApp = loadable(() =>
  import(/* webpackChunkName: "ProtocolsApp" */ './containers/Protocols/App')
)
const TrackingPlanEditorApp = loadable(() =>
  import(/* webpackChunkName: "TrackingPlanEditorApp" */ './containers/Sources/TrackingPlanEditorApp/index')
)
const DataValidationApp = loadable(() =>
  import(/* webpackChunkName: "DataValidationApp" */ './containers/Sources/DataValidationApp/index')
)
const TrackingPlanListApp = loadable(() =>
  import(/* webpackChunkName: "TrackingPlanListApp" */ './containers/Sources/TrackingPlanListApp')
)
const ConnectSourceApp = loadable(() =>
  import(/* webpackChunkName: "ConnectSourceApp" */ './containers/Sources/ConnectSourceApp/index')
)
const SourceSchemaEvents = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaEvents" */ './containers/Sources/SourceSchema/EventsApp')
)
const SourceSchemaIdentifyGroup = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaIdentifyGroup" */ './containers/Sources/SourceSchema/IdentifyGroupApp')
)
const SourceSettingsApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsApp" */ './containers/Sources/Settings/App')
)
const SourceSettingsCrossDomainAnalyticsApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsCrossDomainAnalyticsApp" */ './containers/Sources/Settings/CrossDomainAnalyticsApp')
)
const SourceSettingsCollaboratorsApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsCollaboratorsApp" */ './containers/Sources/Settings/CollaboratorsApp')
)
const SourceSettingsFacebookAdsApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsFacebookAdsApp" */ './containers/Sources/Settings/FacebookAdsApp')
)
const SourceSettingsConnectionApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsConnectionApp" */ './containers/Sources/Settings/ConnectionApp')
)
const SourceSettingsEndpointApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsEndpointApp" */ './components/Sources/Settings/Endpoint')
)
const SourceSettingsSchemaConfigApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsSchemaConfigApp" */ './containers/Sources/Settings/SchemaConfigApp')
)
const SourceSettingsClearSchemaApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsClearSchemaApp" */ './containers/Sources/Settings/ClearSchemaApp')
)
const SourceSettingsBasicApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsBasicApp" */ './containers/Sources/Settings/BasicApp')
)
const SourceSettingsMoveApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsMoveApp" */ './containers/Sources/Settings/MoveApp')
)
const SourceSettingsKeysApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsKeysApp" */ './containers/Sources/Settings/KeysApp')
)
const SourcePluginsApp = loadable(() =>
  import(/* webpackChunkName: "SourcePluginsApp" */ './containers/Sources/Settings/PluginsApp')
)
const SourceSettingsAnalyticsJsApp = loadable(() =>
  import(/* webpackChunkName: "SourceAnalyticsJsApp" */ './containers/Sources/Settings/AnalyticsJsApp')
)
const SourceSettingsAccountViewSettingsApp = loadable(() =>
  import(/* webpackChunkName: "SourceAccountViewSettingsApp" */ './containers/Sources/Settings/AccountViewSettingsApp')
)
const SourceSettingsSqlApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsSqlApp" */ './containers/Sources/Settings/SqlApp')
)
const SourceSettingsSelectiveSyncApp = loadable(() =>
  import(/* webpackChunkName: "SourceSettingsSelectiveSyncApp" */ './containers/Sources/Settings/SelectiveSyncApp')
)
const SourceSchemaEventProperties = loadable(() =>
  import(/* webpackChunkName: "SourceSchemaEventProperties" */ './containers/Sources/SourceSchema/EventPropertiesApp')
)

const WarehouseApp = loadable(() =>
  import(/* webpackChunkName: "WarehouseApp" */ './containers/Warehouses/Warehouse')
)
const WarehouseConnect = loadable(() =>
  import(/* webpackChunkName: "WarehouseConnect" */ './containers/Warehouses/Connect')
)
const WarehouseHistory = loadable(() =>
  import(/* webpackChunkName: "WarehouseHistory" */ './containers/Warehouses/History')
)
const WarehouseOverview = loadable(() =>
  import(/* webpackChunkName: "WarehouseOverview" */ './containers/Warehouses/Overview')
)
const WarehouseRunSheet = loadable(() =>
  import(/* webpackChunkName: "WarehouseRunSheet" */ './containers/Warehouses/RunSheet')
)
const WarehouseSettingsConnection = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsConnection" */ './containers/Warehouses/SettingsConnection')
)
const WarehouseSettingsIndex = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsIndex" */ './containers/Warehouses/Settings')
)
const WarehouseSettingsMaintenance = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsMaintenance" */ './containers/Warehouses/SettingsMaintenance')
)
const WarehouseSettingsSelectiveSync = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsSelectiveSync" */ './containers/Warehouses/SettingsSelectiveSync')
)
const WarehouseSettingsSyncSchedule = loadable(() =>
  import(/* webpackChunkName: "WarehouseSettingsSyncSchedule" */ './containers/Warehouses/SettingsSyncSchedule')
)

const WarehouseSetupApp = loadable(() =>
  import(/* webpackChunkName: "WarehouseSetupApp" */ './containers/Warehouses/SetupApp')
)

const PartnersApp = loadable(() =>
  import(/* webpackChunkName: "PartnersApp" */ './containers/Partners/App')
)
const PartnersCreateApp = loadable(() =>
  import(/* webpackChunkName: "PartnersCreateApp" */ './containers/Partners/CreateApp')
)
const PartnerApp = loadable(() =>
  import(/* webpackChunkName: "PartnerApp" */ './containers/Partners/Partner/App')
)
const PartnerNoteApp = loadable(() =>
  import(/* webpackChunkName: "PartnerNoteApp" */ './containers/Partners/Partner/NoteApp')
)
const PartnerJsonApp = loadable(() =>
  import(/* webpackChunkName: "PartnerJsonApp" */ './containers/Partners/Partner/JsonApp')
)
const PartnerLogosApp = loadable(() =>
  import(/* webpackChunkName: "PartnerLogosApp" */ './containers/Partners/Partner/LogosApp')
)
const PartnerAnalyticsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerAnalyticsApp" */ './containers/Partners/Partner/AnalyticsApp')
)
const PartnerProfileApp = loadable(() =>
  import(/* webpackChunkName: "PartnerProfileApp" */ './containers/Partners/Partner/ProfileApp')
)
const PartnerMethodsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerMethodsApp" */ './containers/Partners/Partner/MethodsApp')
)
const PartnerPlatformsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerPlatformsApp" */ './containers/Partners/Partner/PlatformsApp')
)
const PartnerCategoriesApp = loadable(() =>
  import(/* webpackChunkName: "PartnerCategoriesApp" */ './containers/Partners/Partner/CategoriesApp')
)
const PartnerReplayApp = loadable(() =>
  import(/* webpackChunkName: "PartnerReplayApp" */ './containers/Partners/Partner/ReplayApp')
)
const PartnersReportApp = loadable(() =>
  import(/* webpackChunkName: "PartnersReportApp" */ './containers/Partners/Report/App')
)
const PartnerDirectIntegrationApp = loadable(() =>
  import(/* webpackChunkName: "PartnerDirectIntegrationApp" */ './containers/Partners/Partner/DirectIntegrationApp')
)
const PartnerComponentsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerComponentsApp" */ './containers/Partners/Partner/ComponentsApp')
)
const PartnerContactsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerContactsApp" */ './containers/Partners/Partner/ContactsApp')
)
const PartnerConnectionModeApp = loadable(() =>
  import(/* webpackChunkName: "PartnerConnectionModeApp" */ './containers/Partners/Partner/ConnectionModeApp')
)
const PartnerSettingsApp = loadable(() =>
  import(/* webpackChunkName: "PartnerSettingsApp" */ './containers/Partners/Partner/SettingsApp')
)
const PartnerStatusApp = loadable(() =>
  import(/* webpackChunkName: "PartnerStatusApp" */ './containers/Partners/Partner/StatusApp')
)

function renderRoute(props) {
  /*
    Force a full page reload if app is not on the latest version.
    This will guarantee customers are always on the most recent
    version of the app the moment they decide to navigate away from
    their current path.
  */
  if (!isOnLatest()) {
    const { pathname, search } = props.location
    window.location = `${pathname}${search}`
    return null
  }

  return applyRouterMiddleware(useScroll(scrollBehavior))(props)
}

const PERSONAS = 'personas'
const GROWTH = 'growth'
const PLATFORM_CORE = 'platform-core'
const FOUNDATION_CORE = 'foundation-core'
const DESTINATIONS = 'destinations'
const PROTOCOLS = 'protocols'
const TOOLING = 'tooling'
const SOURCES = 'sources'
const ENTERPRISE_CORE = 'enterprise-core'

const routes = () => (
  <Router
    onUpdate={function storeRoute() {
      /*
        React Router binds this function for us
        That's why we have the named old-school function definition here 😅
      */

      ErrorTracker.setRoute(this.router.routes)
    }}
    history={browserHistory}
    render={renderRoute}
  >
    <Redirect from="/" to="/workspaces" />
    <Route path="/login" component={LoginApp} team={GROWTH} />
    <Route path="/logout" component={Logout} team={GROWTH} />
    <Route path="/signup" component={SignupApp} team={GROWTH} />
    <Route path="/signup-v2" component={SignupAppV2} team={GROWTH} />
    <Route path="/forgot" component={ForgotApp} team={GROWTH} />
    <Route path="/reset" component={ResetApp} team={GROWTH} />
    <Route path="/enable" component={EnableApp} team={GROWTH} />
    <Route path="/workspaces" component={WorkspacesHome} team={GROWTH}>
      <Route path="new" component={WorkspacesNew} />
    </Route>

    <Route
      path="/partner-portal/report"
      component={PartnersReportApp}
      team={PLATFORM_CORE}
    />
    <Redirect from="/partner-portal" to="/partner-portal/integration" />
    <Route
      path="/partner-portal/:metadataType"
      component={PartnersApp}
      team={PLATFORM_CORE}
    >
      <Route path="create" component={PartnersCreateApp} />
      <Route path=":slug" component={PartnerApp}>
        <IndexRedirect to="profile" />
        <Route path="profile" component={PartnerProfileApp} />
        <Route path="status" component={PartnerStatusApp} />
        <Route path="metrics" component={PartnerAnalyticsApp} />
        <Route path="logos" component={PartnerLogosApp} />
        <Route path="json" component={PartnerJsonApp} />
        <Route path="note" component={PartnerNoteApp} />
        <Route path="methods" component={PartnerMethodsApp} />
        <Route path="platforms" component={PartnerPlatformsApp} />
        <Route path="categories" component={PartnerCategoriesApp} />
        <Route path="replay" component={PartnerReplayApp} />
        <Route
          path="direct-integration"
          component={PartnerDirectIntegrationApp}
        />
        <Route path="components" component={PartnerComponentsApp} />
        <Route path="contacts" component={PartnerContactsApp} />
        <Route path="settings" component={PartnerSettingsApp} />
        <Route path="connection-mode" component={PartnerConnectionModeApp} />
      </Route>
    </Route>

    <Route
      path="accounts/:accountSlug"
      component={AccountApp}
      team={ENTERPRISE_CORE}
    />

    <Route path="/:workspaceSlug" component={WorkspaceApp} team={GROWTH}>
      <IndexRedirect to="overview" />
      <Route
        path="welcome"
        component={WelcomeApp}
        disableGlobalTopNav
        disableGlobalSideNav
      />

      <Route
        path="tracking"
        component={WhatToTrack}
        disableGlobalTopNav
        disableGlobalSideNav
      />

      <Route
        path="quickstart(/:sourceSlug)"
        component={WorkspaceQuickStart}
        disableGlobalTopNav
        disableGlobalSideNav
      >
        <Route
          path="destinations(/:destinationSlug)"
          component={WorkspaceQuickStartDestinationSelection}
        />
        <Route
          path="destinations/:destinationSlug/config"
          component={WorkspaceQuickStartDestinationConfig}
        />
        <Route
          path="destinations/:destinationSlug/install"
          component={WorkspaceQuickStartSourceInstall}
        />
      </Route>

      <Route path="settings" component={WorkspaceSettings}>
        <IndexRedirect to="basic" />
        <Route
          path="basic"
          components={WorkspaceSettingsBasicApp}
          label="Settings"
        >
          <Route
            path="delete"
            components={WorkspaceSettingsDeletionApp}
            label="Settings"
          />
        </Route>

        <Redirect from="billing" to="usage" />
        <Redirect from="api-usage" to="usage" />
        <Route
          path="usage"
          components={WorkspaceSettingsUsageApp}
          label="Settings"
        />

        <Route
          path="activity-feed"
          components={WorkspaceSettingsActivityFeedApp}
          label="Settings"
        />

        <Redirect from="notifications-v2" to="notifications" />
        <Route
          path="notifications"
          components={WorkspaceSettingsNotificationsApp}
          label="Settings"
        />

        <Route
          path="connections"
          components={WorkspaceSettingsConnectionsApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="edit-connection/:connectionId"
          components={WorkspaceSettingsConnectionEditApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="setup-connection"
          components={WorkspaceSettingsConnectionSetupApp}
          team={ENTERPRISE_CORE}
        >
          <IndexRedirect to="select" />
          <Route
            path="select"
            components={WorkspaceSettingsConnectionSetupSelectApp}
            label="Settings"
          />

          <Route
            path="configure-connection"
            components={WorkspaceSettingsConnectionSetupConnectionApp}
            label="Settings"
          />

          <Route
            path="configure-idp"
            components={WorkspaceSettingsConnectionSetupIDPApp}
            label="Settings"
          />

          <Route
            path="complete"
            components={WorkspaceSettingsConnectionSetupCompleteApp}
            label="Settings"
          />
        </Route>
        <Route
          path="domains"
          components={WorkspaceSettingsDomainsApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="advanced"
          components={WorkspaceSettingsAdvancedApp}
          label="Settings"
          team={ENTERPRISE_CORE}
        />
        <Route
          path="api-keys"
          components={WorkspaceSettingsApiKeyApp}
          label="Settings"
          team={PLATFORM_CORE}
        />
        <Route
          path="team"
          components={WorkspaceSettingsTeamApp}
          label="Settings"
        />
        <Route
          path="plans"
          components={WorkspaceSettingsPlansApp}
          label="settings"
        >
          <Route
            path="downgrade"
            components={WorkspaceSettingsDowngradeApp}
            label="Settings"
          />
        </Route>
        <Route
          path="card"
          components={WorkspaceSettingsCardApp}
          label="Settings"
        />

        <Route
          path="payments"
          components={WorkspaceSettingsPaymentsApp}
          label="Settings"
        />

        <Redirect
          path="change-plan/developer/*"
          to="change-plan/team-monthly"
        />
        <Route
          path="change-plan/:planId"
          components={WorkspaceSettingsPlanChangeApp}
          label="Settings"
        >
          <IndexRedirect to="select" />
          <Route
            path="select"
            components={WorkspaceSettingsPlanChangeSelectApp}
            ignoreScrollBehavior
            label="Settings"
          />

          <Route
            path="confirm"
            components={WorkspaceSettingsPlanChangeConfirmApp}
            label="Settings"
          />

          <Route
            path="thanks"
            components={WorkspaceSettingsPlanChangeThanksApp}
          />
        </Route>

        <Route
          path="privacy-security"
          components={WorkspaceSettingsPrivacySecurityApp}
          label="Settings"
        />

        <Route
          path="suppressed-users"
          components={WorkspaceSettingsSuppressedUsersApp}
          label="Settings"
          team={FOUNDATION_CORE}
        />

        <Route
          path="deletion-requests"
          components={WorkspaceSettingsDeletionRequestsApp}
          label="Settings"
          team={FOUNDATION_CORE}
        />
      </Route>

      <Route
        path="protocols"
        component={ProtocolsApp}
        disableGlobalTopNav
        team={PROTOCOLS}
      >
        <IndexRedirect to="tracking-plans" />
        <Route
          path="data-validation/:trackingPlanId"
          disableGlobalTopNav
          disableProtocolsTopNav
          component={DataValidationApp}
        />
        <Route path="tracking-plans" label="Protocols">
          <Route path=":trackingPlanId" component={TrackingPlanEditorApp} />
          <Route path="connect/:trackingPlanId" component={ConnectSourceApp} />
          <IndexRoute component={TrackingPlanListApp} />
        </Route>
      </Route>
      <Route
        path="overview"
        component={WorkspaceOverviewApp}
        label="Overview"
      />
      <Redirect from="integrations/catalog" to="destinations/catalog" />
      <Redirect from="warehouses" to="destinations" />
      <Route
        path="destinations"
        component={WorkspaceDestinationsApp}
        label="List of Destinations"
        team={DESTINATIONS}
      />
      <Route
        path="destinations/catalog"
        label="Catalog"
        component={DestinationsCatalogApp}
        breadcrumb={{
          isCatalog: true
        }}
        ignoreScrollBehavior
        team={DESTINATIONS}
      />
      <Route
        path="destinations/catalog/:integrationSlug"
        component={IntegrationsCatalogDetailApp}
        breadcrumb={{
          breadcrumbPath: 'destinations/catalog',
          isCatalogSetup: true
        }}
        team={DESTINATIONS}
      />

      <Route
        path="destinations/warehouses/:warehouseSlug"
        component={WarehouseSetupApp}
        breadcrumb={{
          breadcrumbPath: 'destinations/catalog',
          isCatalogSetup: true
        }}
        team={DESTINATIONS}
      />

      <Route
        path="destinations/:destinationMetadataSlug"
        showBreadcrumb
        team={DESTINATIONS}
      >
        <Route path="overview" component={DestinationOverview} showBreadcrumb />
        <Route
          path="sources/:sourceSlug"
          component={DestinationDetails}
          showBreadcrumb
        >
          <Route path="configuration" component={DestinationConfig} />
          <Redirect from="simulator" to="validator" />
          <Redirect from="validator" to="event-tester" />
          <Route
            path="event-tester"
            component={DestinationDebugger}
            team={TOOLING}
          />
          <Redirect from="deliverability" to="event-delivery" />
          <Redirect from="deliverability/:type" to="event-delivery/:type" />
          <Route
            path="event-delivery"
            component={DestinationEventDelivery}
            ignoreScrollBehavior
            team={PLATFORM_CORE}
          >
            <Route path=":type" component={DestinationErrorSheet} />
          </Route>
        </Route>
      </Route>

      <Redirect
        from="sources/:sourceSlug/overview/destinations/:integrationSlug"
        to="destinations/:integrationSlug/sources/:sourceSlug/configuration"
      />

      <Route
        path="sources"
        component={SourcesApp}
        label="List of Sources"
        team={SOURCES}
      />
      <Route
        path="sources/catalog(/:sourceMetadataSlug)"
        label="Catalog"
        component={SourcesCatalogApp}
        breadcrumb={{
          isCatalog: true
        }}
        ignoreScrollBehavior
        team={SOURCES}
      />
      <Route
        path="sources/setup/:sourceMetadataSlug(/:step)"
        component={LegacySourceSetup}
        breadcrumb={{
          breadcrumbPath: 'sources/catalog',
          isCatalogSetup: true
        }}
        team={SOURCES}
      />
      <Route
        path="sources/:sourceSlug/schema2/events/:eventId"
        component={SourceSchemaEventProperties}
        showBreadcrumb
        team={PROTOCOLS}
      />
      <Route
        path="sources/:sourceSlug"
        component={SourceApp}
        showBreadcrumb
        team={SOURCES}
      >
        <IndexRedirect to="overview" />
        <Redirect from="integrations" to="overview" />
        <Redirect
          from="integrations/:integrationSlug"
          to="destinations/:integrationSlug"
        />
        <Route path="overview" component={SourceOverviewApp}>
          <Route path="runs/:runId" component={SourceOverviewRun} />
        </Route>
        <Route
          path="warehouse-simulator"
          component={WarehouseSimulatorApp}
          team={GROWTH}
        />
        <Route path="debugger" component={SourceDebugger} team={PROTOCOLS} />
        <Redirect from="schema" to="schema/tracks" />
        <Route
          path="schema(/:pageSlug(/:eventName))"
          component={SourceSchemaOld}
          team={PROTOCOLS}
        />
        <Redirect from="schema2" to="schema2/events" />
        <Route path="schema2" team={PROTOCOLS}>
          <IndexRedirect to="events" />
          <Route path="events" component={SourceSchemaEvents} />
          <Route path="identify" component={SourceSchemaIdentifyGroup} />
          <Route path="group" component={SourceSchemaIdentifyGroup} />
          <Route path="violations" component={ViolationsApp}>
            <Route path=":id" component={ViolationSummarySheet} />
          </Route>
        </Route>
        <Route path="settings" component={SourceSettingsApp}>
          <IndexRedirect to="basic" />
          <Route
            path="cross-domain-analytics"
            component={SourceSettingsCrossDomainAnalyticsApp}
          />
          <Route
            path="collaborators"
            component={SourceSettingsCollaboratorsApp}
          />
          <Route path="oauth" component={SourceSettingsFacebookAdsApp} />
          <Route path="connection" component={SourceSettingsConnectionApp} />
          <Route path="endpoint" component={SourceSettingsEndpointApp} />
          <Route
            path="schema"
            component={SourceSettingsSchemaConfigApp}
            team={PROTOCOLS}
          />
          <Route
            path="schema/clear"
            component={SourceSettingsClearSchemaApp}
            team={PROTOCOLS}
          />
          <Route path="basic" component={SourceSettingsBasicApp} />
          <Route path="move" component={SourceSettingsMoveApp} />
          <Route path="keys" component={SourceSettingsKeysApp} />
          <Route path="sql" component={SourceSettingsSqlApp} />
          <Route path="plugins" component={SourcePluginsApp} />
          <Route path="analytics-js" component={SourceSettingsAnalyticsJsApp} />
          <Route
            path="account-setup"
            component={SourceSettingsAccountViewSettingsApp}
          />
          <Route
            path="selective-sync"
            component={SourceSettingsSelectiveSyncApp}
          />
        </Route>
      </Route>

      <Redirect path="customers" to="personas" />
      <Redirect path="customers/explorer" to="personas/explorer" />
      <Redirect path="customers/traits" to="personas/traits" />

      <Route
        path="personas"
        component={CustomersApp}
        label="Personas"
        disableGlobalTopNav
        team={PERSONAS}
      >
        <IndexRedirect to="explorer" />
        <Route path="explorer" component={CustomersExplorerApp}>
          <Route path=":profileId" component={CustomersProfileSheetApp} />
        </Route>
        <Route path="new" component={CustomersCreateApp} />
        <Route path="traits" component={CustomersTraitsListApp} />
        <Route path="traits/new" component={CustomersTraitsCreateApp} />
        <Route path="traits/:traitId" component={CustomersTraitsViewApp}>
          <IndexRedirect to="overview" />
          <Route path="overview" component={CustomersTraitsOverviewApp} />
          <Route path="edit" component={CustomersTraitsEditApp} />
          <Route path="settings" component={CustomersTraitsSettingsApp} />
        </Route>
        <Route path="destinations" component={CustomersDestinationsApp}>
          <Route
            path=":destinationId"
            component={CustomersDestinationsViewApp}
          />
        </Route>
        <Route path="settings" component={CustomersSettings}>
          <IndexRedirect to="sources" />
          <Route path="sources" component={CustomersSettingsSourcesApp} />
          <Route path="api-access" component={CustomersSettingsAPIAccessApp} />
        </Route>

        <Route path="audiences" component={CustomersAudiencesListApp} />
        <Route path="audiences/new" component={CustomersAudiencesCreateApp} />
        <Route
          path="audiences/:audienceId"
          component={CustomersAudiencesViewApp}
        >
          <IndexRedirect to="overview" />
          <Route path="overview" component={CustomersAudiencesOverviewApp} />
          <Route path="edit" component={CustomersAudiencesEditApp} />
          <Route path="settings" component={CustomersAudiencesSettingsApp} />
        </Route>
      </Route>

      <Route
        path="warehouses/:warehouseId"
        component={WarehouseApp}
        showBreadcrumb
        team={DESTINATIONS}
      >
        <IndexRedirect to="overview" />
        <Route path="overview" component={WarehouseOverview} />
        <Route path="connect(/:bitoolSlug)" component={WarehouseConnect} />

        <Route path="settings" component={WarehouseSettingsIndex}>
          <Route path="connection" component={WarehouseSettingsConnection} />
          <Route path="maintenance" component={WarehouseSettingsMaintenance} />
          <Route path="sync" component={WarehouseSettingsSelectiveSync} />
          <Route
            path="sync-schedule"
            component={WarehouseSettingsSyncSchedule}
          />
        </Route>

        <Route path="sync" component={WarehouseHistory} />
        <Route
          path="sync/:sourceId"
          component={WarehouseHistory}
          showBreadcrumb
        >
          <Route path=":runId" component={WarehouseRunSheet} />
        </Route>
      </Route>
    </Route>
    <Route
      path="/:workspaceSlug/settings/billing/payments/:chargeId"
      component={ReceiptApp}
      team={GROWTH}
    />
    <Route path="*" component={NotFound} team={GROWTH} />
  </Router>
)

export default routes
